import { useCallback, useRef, useEffect, useState } from "react";
import {
  trackEvent,
  EVENT_CATEGORY,
  EVENT_TYPE
} from "@lululemon/mwa-analytics";

// CONSTANTS
import { COMPLETED_PERCENTAGE, HALFWAY_PERCENTAGE } from "../utils/constants";

const useVideoPlayerAnalyticsTracking = ({
  position,
  text,
  totalCount,
  type,
  videoId
}) => {
  const [videoRef, setVideoRef] = useState(null);
  const intervalRef = useRef(null);
  const isPlayStarted = useRef(false);
  const isPastHalf = useRef(false);
  const isAboutToEnd = useRef(false);

  const trackAnalytics = useCallback(
    (videoMarker, videoLength) => {
      if (videoMarker >= COMPLETED_PERCENTAGE && !isAboutToEnd.current) {
        // send an analytics event when video has been played to 95% completion
        isAboutToEnd.current = true;
        trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
          eventSubType: EVENT_TYPE.INTERACTION,
          component: {
            id: "video-marker",
            type,
            text,
            position,
            totalCount
          },
          event: {
            attributes: {
              videoMarker,
              videoId: videoId,
              videoLength: `${videoLength}`
            }
          }
        });
      } else if (videoMarker >= HALFWAY_PERCENTAGE && !isPastHalf.current) {
        // send an analytics event when video has been played to 50% completion
        isPastHalf.current = true;
        trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
          eventSubType: EVENT_TYPE.INTERACTION,
          component: {
            id: "video-marker",
            type,
            text,
            position,
            totalCount
          },
          event: {
            attributes: {
              videoMarker,
              videoId: videoId,
              videoLength: `${videoLength}`
            }
          }
        });
      } else if (!isPlayStarted.current) {
        // send an analytics event when the video starts playing
        isPlayStarted.current = true;
        trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
          eventSubType: EVENT_TYPE.INTERACTION,
          component: {
            id: "video-play",
            type,
            text,
            position,
            totalCount
          },
          event: {
            attributes: {
              videoId: videoId,
              videoLength: `${videoLength}`
            }
          }
        });
      }
    },
    [position, text, totalCount, type, videoId]
  );

  const scheduleTrackAnalytics = useCallback(() => {
    if (videoRef) {
      if (!isPlayStarted.current) {
        trackAnalytics(0, videoRef.duration);
        scheduleTrackAnalytics();
      } else if (!isPastHalf.current) {
        clearTimeout(intervalRef.current);
        const trackFn = trackAnalytics.bind(
          null,
          HALFWAY_PERCENTAGE,
          videoRef.duration
        );
        // video duration and currentTime are represented in seconds
        const timeToNextEventInMs =
          (videoRef.duration * (HALFWAY_PERCENTAGE / 100) -
            videoRef.currentTime) *
          1000;
        intervalRef.current = setTimeout(() => {
          trackFn();
          scheduleTrackAnalytics();
        }, timeToNextEventInMs);
      } else if (!isAboutToEnd.current) {
        clearTimeout(intervalRef.current);
        const trackFn = trackAnalytics.bind(
          null,
          COMPLETED_PERCENTAGE,
          videoRef.duration
        );
        // video duration and currentTime are represented in seconds
        const timeToNextEventInMs =
          (videoRef.duration * (COMPLETED_PERCENTAGE / 100) -
            videoRef.currentTime) *
          1000;
        intervalRef.current = setTimeout(() => {
          trackFn();
          scheduleTrackAnalytics();
          // 'seeked' event continues to fire once for every loop of autoplay video, so we can
          // cancel the event once we're done
          videoRef.removeEventListener("seeked", scheduleTrackAnalytics);
        }, timeToNextEventInMs);
      }
    }
  }, [videoRef, trackAnalytics]);

  useEffect(() => {
    if (videoRef && !isAboutToEnd.current) {
      const playHandler = () => {
        scheduleTrackAnalytics();
      };

      const pauseHandler = () => {
        clearTimeout(intervalRef.current);
      };

      videoRef.addEventListener("play", playHandler);
      videoRef.addEventListener("pause", pauseHandler);
      videoRef.addEventListener("seeked", scheduleTrackAnalytics);

      // clean-up timers and event listers when element is unmounted
      return () => {
        clearTimeout(intervalRef.current);

        videoRef.removeEventListener("play", playHandler);
        videoRef.removeEventListener("pause", pauseHandler);
        videoRef.removeEventListener("seeked", scheduleTrackAnalytics);
      };
    }
  }, [videoRef, trackAnalytics, scheduleTrackAnalytics]);

  return useCallback(
    (ref) => {
      if (videoRef !== ref) {
        setVideoRef(ref);
      }
    },
    [videoRef]
  );
};

export default useVideoPlayerAnalyticsTracking;
